import { useCallback, useEffect, useRef, useState } from "react";
import { useSingleEffect } from "../../../../lib/hooks/SingleEffect.hook";
import Icon from "../../../commons/Icon";
import { libMisc } from "../../../../lib/misc";
import { s } from "../../../../lib/styler";
import { LOADING_DIRECTIVE, serviceSync } from "../../../../services/sync.service";
import { useAtom } from "jotai";

export type MessagingThreadSenderProps = {
    initialText: string;
    initialImageBlobIds: number[];
    onTextAreaLineCountChange?: (count: number) => void;
};

// Define a local type for the items to be sent, matching SyncService's internal type
interface MessageToSendItem {
    type: "text" | "media";
    value: string | { blob: Blob; ext: string };
    direction: "from" | "to";
}

export default function MessagingThreadSender({
    initialText,
    initialImageBlobIds,
    onTextAreaLineCountChange,
}: MessagingThreadSenderProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [text, setText] = useState(initialText);
    const [imageBlobIds, setImageBlobIds] = useState<number[]>(initialImageBlobIds);
    const [isAiThinking] = useAtom(serviceSync.atomIsAiThinking);
    const disabled = (text.trim() === "" && imageBlobIds.length === 0) || isAiThinking;

    useSingleEffect(() => {
        textAreaRef.current?.focus();
    });

    const sendClickHandler = useCallback(async () => {
        if (disabled) {
            return;
        }

        const messagesToSend: MessageToSendItem[] = [];
        const currentImageBlobIds = [...imageBlobIds]; // Capture current IDs before clearing UI state

        // Media blobs first
        for (const id of currentImageBlobIds) {
            const draftMediaBlob = serviceSync.getDraftMediaBlob(id);
            if (draftMediaBlob) {
                messagesToSend.push({
                    type: "media",
                    value: { blob: draftMediaBlob.blob, ext: draftMediaBlob.ext },
                    direction: "to",
                });
            }
        }

        // Text last, if any
        const trimmedText = text.trim();
        if (trimmedText !== "") {
            messagesToSend.push({
                type: "text",
                value: trimmedText,
                direction: "to",
            });
        }

        if (messagesToSend.length > 0) {
            messagesToSend.push({
                type: "text",
                value: LOADING_DIRECTIVE,
                direction: "from",
            });
            await serviceSync.sendMultiMessages(messagesToSend);

            // Clean up draft media blobs that were part of this send operation
            for (const id of currentImageBlobIds) {
                await serviceSync.removeDraftMediaBlob(id);
            }
        }

        setText("");
        setImageBlobIds([]);
        await serviceSync.updateDraft("", []);

    }, [text, disabled, imageBlobIds, isAiThinking]);

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const isEnter = e.key === "Enter";
        const isCmdOrCtrl = libMisc.isMac ? e.metaKey : e.ctrlKey;

        if (isEnter && isCmdOrCtrl) {
            e.preventDefault();
            sendClickHandler();
        }
    };

    const handleImageAddClick = useCallback(() => {
        inputRef.current?.click();
    }, []);

    const handleFilesChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const blobIds: number[] = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.type === "image/jpeg" || file.type === "image/png") {
                const ext = file.name.split(".").pop() || "";
                const id = await serviceSync.addDraftMediaBlob(file, ext);
                blobIds.push(id);
            } else {
                console.warn(`Skipped unsupported file type: ${file.type}`);
            }
        }

        const newImageBlobIds = [...imageBlobIds, ...blobIds];
        setImageBlobIds(newImageBlobIds);
        await serviceSync.updateDraft(text, newImageBlobIds);

        e.target.value = "";
        textAreaRef.current?.focus();
    }, [text, imageBlobIds]);

    const handleRemoveImageClick = useCallback(async (index: number) => {
        await serviceSync.removeDraftMediaBlob(imageBlobIds[index]);
        imageBlobIds.splice(index, 1);
        const newImageBlobIds = [...imageBlobIds];
        setImageBlobIds(newImageBlobIds);
        await serviceSync.updateDraft(text, newImageBlobIds);
        textAreaRef.current?.focus();
    }, [text, imageBlobIds]);

    return (
        <div className={`h-full flex pl-[20px] pr-[10px] py-[20px] relative`}>
            <div className="absolute top-0 left-0 w-full h-0">
                <div className="absolute bottom-0 left-0 w-full h-[80px] pointer-events-none overflow-y-hidden">
                    <div className={s("w-full h-full bg-black/40 transition-transform duration-100 pointer-events-auto overflow-x-auto flex", {}, [
                        ["translate-y-0", "translate-y-full", imageBlobIds.length > 0]
                    ])}>
                        <div className="shrink-0 h-full w-[15px]"></div>
                        <div className="flex flex-nowrap h-full gap-[20px] py-[15px]">
                            {imageBlobIds.map((id, index) => (
                                <div key={index} className="shrink-0 h-full basis-[65px] border-[2px] border-white bg-white relative">
                                    <img className="h-full w-full object-cover object-center" src={serviceSync.getDraftMediaBlob(id)?.url || ""} />
                                    <div className="absolute left-0 top-0 w-full h-full bg-black/50 flex justify-center items-center text-white opacity-0 hover:opacity-100 duration-200 cursor-pointer" onClick={() => handleRemoveImageClick(index)}>
                                        <Icon name="bx-x" size="30px" />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="shrink-0 h-full w-[15px]"></div>
                    </div>
                </div>
            </div>
            <div className="flex items-center pr-[15px] text-gray-500 hover:text-[#caa24d] duration-200 cursor-pointer">
                <Icon name="bxs-image-add" size="30px" onClick={handleImageAddClick} activeExpandX={11} activeExpandY={9} />
                <input
                    className="hidden"
                    ref={inputRef}
                    type="file"
                    accept="capture=camera,image/*"
                    multiple
                    onChange={handleFilesChange}
                />
            </div>
            <div className="flex-grow">
                <textarea
                    ref={textAreaRef}
                    className="text-gray-700 p-4 text-[14px] border border-gray-300 resize-none bg-white rounded-lg w-full h-full outline-none focus:outline-none focus:ring-0"
                    value={text}
                    onChange={(e) => {
                        const count = e.target.value.split("\n").length;
                        onTextAreaLineCountChange?.(count);
                        setText(e.target.value);
                        serviceSync.updateDraft(e.target.value, imageBlobIds);
                    }}
                    onKeyDown={onKeyDownHandler}
                />
            </div>
            <div className="flex flex-col justify-end">
                <div
                    className={
                        s("p-[15px] transition-colors duration-200", {}, [
                            ["text-gray-300", "text-gray-500 hover:text-[#caa24d] cursor-pointer", disabled]
                        ])
                    }
                    onClick={sendClickHandler}
                >
                    <Icon disabled={disabled} name="bxs-send" size="30px" activeExpandX={11} activeExpandY={8} />
                </div>
            </div>
        </div>
    );
}